import { useCallback, useEffect, useState, type ReactNode } from "react";
import { ArrowRight, Fingerprint, Loader2 } from "lucide-react";
import { maskLogin, passwordRules, useAutomaticSsoSignIn, useMfaRequired, useOptionalAuth, useSsoFailure, useSsoProviders, useSsoSignIn, usePasswordSignIn, type SessionResponse, type SignInSurface, type SsoProvider } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Button, InlineAlert, cn } from "@ncp/ui";
import { StaffSignInCard } from "@ncp/ui/components/signin";

function MicrosoftMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 21 21" aria-hidden className={cn("size-4 shrink-0", className)}>
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

/** An identity provider's logo: its configured icon, the Microsoft mark for Entra ID, a fingerprint otherwise. */
export function IdentityProviderMark({ kind, iconUrl, className }: { kind: string; iconUrl?: string | null; className?: string }) {
  if (iconUrl) return <img src={iconUrl} alt="" className={cn("size-4 shrink-0 object-contain", className)} />;
  return kind === "EntraId" ? <MicrosoftMark className={className} /> : <Fingerprint className={cn("size-4 shrink-0 text-aegold-700", className)} />;
}

const REASONS = ["idle", "expired", "revoked", "ended", "other_tab"];

/**
 * Why the person is on the sign-in page: `/login?reason=idle|expired|revoked|ended|other_tab`, or the device's last end record (a sign-out
 * in another tab may reach this tab as a storage change before its message), shown once. A tab's own "Sign out" needs no reason.
 */
export function useSignInReason(): string | null {
  const auth = useOptionalAuth();
  const [reason] = useState(() => {
    if (typeof window === "undefined") return null;
    const r = new URLSearchParams(window.location.search).get("reason");
    if (r && REASONS.includes(r)) return r;
    const last = auth?.controller.lastEnd();
    if (!last) return null;
    if (last.reason === "signed_out") return last.mine ? null : "other_tab";
    return REASONS.includes(last.reason) ? last.reason : null;
  });
  useEffect(() => {
    auth?.controller.consumeEnd();
  }, [auth?.controller]);
  return reason;
}

/** The message of a failed return from the Ministry account (kept in session storage by `SsoCallbackGate`), shown once. */
export function useSsoFailureText(): [string | null, () => void] {
  const { t } = useTranslation();
  const [failure, clear] = useSsoFailure();
  return [failure ? t(`sso.errors.${failure.code}`, { defaultValue: failure.message || t("sso.errors.generic") }) : null, clear];
}

/**
 * "Sign in with Ministry account": one button per live directory provider. Nothing
 * renders where none is configured (the laptop). Simulated providers and account pickers are gone (security S2).
 */
export function MinistrySignIn({ from, className, variant = "outline", buttonClassName }: { from?: string | null; className?: string; variant?: "outline" | "default"; buttonClassName?: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { providers } = useSsoProviders();
  const { start } = useSsoSignIn();
  const automatic = useAutomaticSsoSignIn(providers, from);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const nameOf = (p: SsoProvider) => (locale === "ar" && p.nameAr ? p.nameAr : p.name);

  const begin = async (provider: SsoProvider) => {
    if (busy || automatic) return;
    setError(null);
    setBusy(provider.key);
    try {
      await start(provider.key, from);
    } catch {
      setError(t("sso.errors.provider_unavailable"));
      setBusy(null);
    }
  };

  if (providers.length === 0) return null;
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {error ? <InlineAlert tone="danger" className="text-start">{error}</InlineAlert> : null}
      {providers.map((p, i) => (
        <Button
          key={p.key}
          type="button"
          variant={variant}
          size="lg"
          data-testid={i === 0 ? "sso-keycloak" : `sso-${p.key}`}
          className={cn("h-auto min-h-11 w-full justify-start whitespace-normal px-4 py-2 text-sm", buttonClassName)}
          disabled={busy !== null || automatic}
          onClick={() => void begin(p)}
        >
          {busy === p.key || automatic ? <Loader2 className="animate-spin" /> : <IdentityProviderMark kind={p.kind} iconUrl={p.iconUrl} />}
          <span className="min-w-0 flex-1 text-start leading-5">{busy === p.key || automatic ? t("sso.redirecting", { name: nameOf(p) }) : providers.length === 1 ? t("auth.ministry") : t("sso.continueWith", { name: nameOf(p) })}</span>
          <ArrowRight className="text-sand-500 rtl:-scale-x-100" />
        </Button>
      ))}
    </div>
  );
}

/**
 * Kept for the pages that still wrap their sign-in in it: the Ministry-account buttons and a failed return, then `children`.
 * The persona list it used to sit beside is gone; new screens use `StaffSignIn` or the ECONIC sign-in card.
 */
export function SsoSignIn({ from, className, children }: { from?: string | null; className?: string; children?: ReactNode }) {
  const [failure] = useSsoFailureText();
  return (
    <div className={className}>
      {failure ? <InlineAlert tone="danger" className="mb-4 text-start">{failure}</InlineAlert> : null}
      <MinistrySignIn from={from} />
      {children}
    </div>
  );
}

/**
 * The staff sign-in of the back office and the Platform Console: password (→ new password on a first sign-in), with the reason the
 * person was signed out and the Ministry-account button where one exists. A host that sets `Auth:RequireMfa` adds the authenticator
 * enrolment and code. ECONIC uses its own card on the same flow.
 */
export function StaffSignIn({ surface, from, heading, onSignedIn, className }: {
  surface: Exclude<SignInSurface, "assistant">;
  from?: string | null;
  heading?: ReactNode;
  onSignedIn?: (s: SessionResponse) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const flow = usePasswordSignIn({ surface, onSignedIn });
  const reason = useSignInReason();
  const [failure] = useSsoFailureText();
  const { providers, loading } = useSsoProviders();
  const tt = useCallback((key: string, vars?: Record<string, unknown>) => String(t(`auth.${key}`, (vars ?? {}) as Record<string, string>)), [t]);
  const rules = useCallback((pw: string, confirm: string) => passwordRules(pw, confirm, flow.login), [flow.login]);
  const notice = failure ? <InlineAlert tone="danger" className="text-start">{failure}</InlineAlert> : reason && flow.step === "password" && !flow.error ? <InlineAlert tone={reason === "revoked" ? "warning" : "info"} className="text-start">{t(`auth.reason.${reason}`)}</InlineAlert> : null;
  const mfa = useMfaRequired() || flow.step === "mfa" || flow.step === "mfa_enrol";
  return <StaffSignInCard flow={flow} t={tt} maskedLogin={maskLogin(flow.login)} rules={rules} notice={notice} heading={heading} mfa={mfa} ministry={providers.length ? <MinistrySignIn from={from} variant="default" /> : null} ministryLoading={loading} className={className} />;
}
